import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

const Container = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const PresetButton = styled.button`
  padding: 8px 16px;
  border: 1px solid #ddd;
  background: ${props => props.active ? '#007bff' : 'white'};
  color: ${props => props.active ? 'white' : '#333'};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? '#0056b3' : '#f8f9fa'};
  }
`;

const DateInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
`;

const RefreshButton = styled.button`
  padding: 8px 16px;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: #218838;
  }
  
  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
  }
`;

export const DateRangePicker = ({ onDateChange, onPresetChange, loading, initialPreset = '28days', initialDateRange }) => {
  const [preset, setPreset] = useState(initialPreset);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  // Sync internal preset state with prop changes
  useEffect(() => {
    setPreset(initialPreset);
  }, [initialPreset]);

  // Sync custom date fields with initialDateRange when switching to custom
  useEffect(() => {
    if (preset === 'custom' && initialDateRange) {
      setCustomFrom(initialDateRange.from || '');
      setCustomTo(initialDateRange.to || '');
    }
  }, [preset, initialDateRange]);

  // Analytics data collection started on July 1st, 2025
  const ANALYTICS_START_DATE = '2025-07-01';

  const getYesterday = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  };

  const getDateNDaysAgo = (days) => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date.toISOString().split('T')[0];
  };

  const presets = [
    { key: '7days', label: '7 Days', from: getDateNDaysAgo(7), to: getYesterday() },
    { key: '28days', label: '28 Days', from: getDateNDaysAgo(28), to: getYesterday() },
    { key: '90days', label: '90 Days', from: getDateNDaysAgo(90), to: getYesterday() },
    { key: 'alltime', label: 'All Time', from: ANALYTICS_START_DATE, to: getYesterday() },
    { key: 'custom', label: 'Custom Range' }
  ];

  const handlePresetClick = (presetKey) => {
    setPreset(presetKey);
    
    // Notify parent component about preset change
    if (onPresetChange) {
      onPresetChange(presetKey);
    }
    
    if (presetKey !== 'custom') {
      const selectedPreset = presets.find(p => p.key === presetKey);
      const from = selectedPreset.from;
      const to = selectedPreset.to;
      onDateChange(from, to);
    }
  };

  const handleCustomDateChange = () => {
    if (customFrom && customTo) {
      onDateChange(customFrom, customTo);
    }
  };

  return (
    <Container>
      {presets.map(p => (
        <PresetButton
          key={p.key}
          active={preset === p.key}
          onClick={() => handlePresetClick(p.key)}
        >
          {p.label}
        </PresetButton>
      ))}
      
      {preset === 'custom' && (
        <>
          <DateInput
            type="date"
            value={customFrom}
            min={ANALYTICS_START_DATE}
            max={getYesterday()}
            onChange={(e) => setCustomFrom(e.target.value)}
            placeholder="From"
          />
          <DateInput
            type="date"
            value={customTo}
            min={customFrom || ANALYTICS_START_DATE}
            max={getYesterday()}
            onChange={(e) => setCustomTo(e.target.value)}
            placeholder="To"
          />
          <RefreshButton
            onClick={handleCustomDateChange}
            disabled={!customFrom || !customTo || loading}
          >
            {loading ? 'Loading...' : 'Apply'}
          </RefreshButton>
        </>
      )}
    </Container>
  );
};

DateRangePicker.propTypes = {
  onDateChange: PropTypes.func.isRequired,
  onPresetChange: PropTypes.func,
  loading: PropTypes.bool,
  initialPreset: PropTypes.string,
  initialDateRange: PropTypes.shape({
    from: PropTypes.string,
    to: PropTypes.string
  })
};
